import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Option,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { useCallback, useState } from 'react';

import type { AnnotationSchema } from '../../../../services/dataHubService.interface.js';
import { useStyles } from '../DatasetAnnotationsTab.style.js';

export interface CreateAnnotationSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schemas: AnnotationSchema[];
  isCreating: boolean;
  onCreate: (name: string, schemaId: string, description?: string) => void;
}

export function CreateAnnotationSetDialog({
  open,
  onOpenChange,
  schemas,
  isCreating,
  onCreate,
}: CreateAnnotationSetDialogProps) {
  const styles = useStyles();

  const [name, setName] = useState('');
  const [schemaId, setSchemaId] = useState('');
  const [description, setDescription] = useState('');

  const selectedSchema = schemas.find((s) => s.id === schemaId);

  const resetForm = useCallback(() => {
    setName('');
    setSchemaId('');
    setDescription('');
  }, []);

  const handleCreate = useCallback(() => {
    onCreate(name.trim(), schemaId, description.trim() || undefined);
    resetForm();
  }, [name, schemaId, description, onCreate, resetForm]);

  const canCreate = name.trim().length > 0 && schemaId.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        onOpenChange(data.open);
        if (!data.open) resetForm();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New Annotation Set</DialogTitle>
          <DialogContent>
            <div className={styles.dialogForm}>
              <Field label="Name" required>
                <Input
                  value={name}
                  onChange={(_, d) => setName(d.value)}
                  placeholder="My annotation set"
                />
              </Field>
              <Field
                label="Schema"
                required
                hint="Native set compatibility schema. Managed annotation files record their own editor schemas."
              >
                <Dropdown
                  value={selectedSchema?.name ?? ''}
                  selectedOptions={schemaId ? [schemaId] : []}
                  onOptionSelect={(_, d) =>
                    d.optionValue !== undefined && setSchemaId(d.optionValue)
                  }
                  placeholder="Select a schema…"
                >
                  {schemas.map((s) => (
                    <Option key={s.id} value={s.id}>
                      {s.name}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Description">
                <Textarea
                  value={description}
                  onChange={(_, d) => setDescription(d.value)}
                  rows={3}
                />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isCreating}
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canCreate || isCreating}
              icon={isCreating ? <Spinner size="tiny" /> : undefined}
              onClick={handleCreate}
            >
              {isCreating ? 'Creating…' : 'Create'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
